<div class="w-full">
  <!-- Toolbar Header -->
  <div class="glass-card p-4 rounded-2xl mb-6 relative z-100">
    <div class="flex flex-col md:flex-row gap-4 items-start md:items-center">
      <!-- Search Input -->
      <div class="relative flex-1 max-w-md">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <span class="material-icons text-xl text-text-muted">search</span>
        </div>
        <input
          type="text"
          [(ngModel)]="searchTerm"
          (input)="onSearchChange()"
          placeholder="Suche nach Nr., Kunde, Bauteilart..."
          class="search-input w-full pl-12 pr-10 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary placeholder-text-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
        />
        @if (searchTerm) {
          <button
            type="button"
            class="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-text-primary transition-colors"
            (click)="clearSearch()"
          >
            <span class="material-icons text-xl">close</span>
          </button>
        }
      </div>

      <!-- Status Filter -->
      <div class="flex items-center gap-2">
        <span class="material-icons text-text-muted">filter_list</span>
        <p-multiSelect
          [(ngModel)]="statusFilter"
          [options]="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Status filtern"
          [filter]="false"
          [showClear]="true"
          [display]="'chip'"
          [maxSelectedLabels]="4"
          styleClass="multiselect-component-style"
          panelStyleClass="multiselect-component-panel-style"
          (onChange)="onStatusFilterChange()"
        >
          <ng-template pTemplate="item" let-option>
            <div class="option-row">
              <span class="option-label">{{ option.label }}</span>
            </div>
          </ng-template>
        </p-multiSelect>
      </div>

      @if (canCreate) {
        <div class="md:ml-auto">
          <button type="button" class="btn-primary flex items-center gap-2" (click)="openCreatePage()">
            <span class="material-icons text-xl">add</span>
            Auftragsbegleitschein erstellen
          </button>
        </div>
      }
    </div>
  </div>

  <!-- Order Documents Table -->
  <div class="glass-card rounded-2xl overflow-hidden">
    <!-- Loading State -->
    @if (isLoading) {
      <div class="flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-white/10">
              <th class="table-header">
                <div class="flex items-center gap-1">Auftrag-Nr.</div>
              </th>
              <th class="table-header">
                <div class="flex items-center gap-1">Kunde</div>
              </th>
              <th class="table-header">
                <div class="flex items-center gap-1">Liefertermin</div>
              </th>
              <th class="table-header" (click)="onSort('orderQuantity')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Auftragsmenge
                  <span class="material-icons text-lg">{{ getSortIcon('orderQuantity') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('componentType')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Bauteilart
                  <span class="material-icons text-lg">{{ getSortIcon('componentType') }}</span>
                </div>
              </th>
              <th class="table-header">
                <div class="flex items-center gap-1">Bearbeitung</div>
              </th>
              <th class="table-header" (click)="onSort('status')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Status
                  <span class="material-icons text-lg">{{ getSortIcon('status') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('weDate')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  WE-Datum
                  <span class="material-icons text-lg">{{ getSortIcon('weDate') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('factory')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Werk
                  <span class="material-icons text-lg">{{ getSortIcon('factory') }}</span>
                </div>
              </th>
              <th class="table-header">
                <span class="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            @if (orderDocuments.length === 0) {
              <tr>
                <td colspan="10" class="px-6 py-12 text-center text-text-muted">Keine Auftragsbegleitscheine gefunden...</td>
              </tr>
            } @else {
              @for (doc of orderDocuments; track doc._id) {
                <tr class="table-row border-b border-white/6 last:border-b-0 cursor-pointer hover:bg-white/4" (click)="openEditPage(doc)">
                  <td class="table-cell">
                    @if (doc.order) {
                      <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ doc.order.internalOrderId }}</span>
                    } @else {
                      <span class="text-text-muted">—</span>
                    }
                  </td>
                  <td class="table-cell text-text-secondary">
                    <span class="flex max-w-40 items-center gap-1.5">
                      <span class="truncate">{{ getCustomerLabel(doc) }}</span>
                      @if (isTempCustomer(doc)) {
                        <span
                          class="shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-400/15 text-amber-400"
                          title="Temporärer Kunde – noch nicht als Auftrag im System angelegt"
                          >temp</span
                        >
                      }
                    </span>
                  </td>
                  <td class="table-cell">
                    <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ getNextLieferdatum(doc) }}</span>
                  </td>
                  <td class="table-cell text-text-secondary">{{ getOrderQuantityDisplay(doc) }}</td>
                  <td class="table-cell text-text-secondary">{{ doc.componentType || '—' }}</td>
                  <td class="table-cell">
                    <div class="flex gap-1">
                      @for (p of doc.processing; track p) {
                        <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-400/15 text-blue-400 w-fit">
                          {{ getProcessingLabel(p) }}
                        </span>
                      }
                      @for (a of doc.activities; track a) {
                        <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-400/15 text-purple-400 w-fit">
                          {{ getActivityLabel(a) }}
                        </span>
                      }
                      @if (!doc.processing?.length && !doc.activities?.length) {
                        <span class="text-text-muted">—</span>
                      }
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center gap-2">
                      <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold" [ngClass]="getStatusClass(doc.status)">
                        {{ getStatusName(doc.status) }}
                      </span>
                      @if (getNextStatus(doc.status)) {
                        <button
                          type="button"
                          class="p-1 rounded-lg flex text-text-muted hover:text-emerald-400 hover:bg-emerald-400/10 transition-all"
                          (click)="onQuickStatusChange(doc, getNextStatus(doc.status)!); $event.stopPropagation()"
                          [title]="'Zu ' + getStatusName(getNextStatus(doc.status)!) + ' ändern'"
                        >
                          <span class="material-icons text-lg">arrow_forward</span>
                        </button>
                      }
                    </div>
                  </td>
                  <td class="table-cell">
                    <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ formatDate(doc.weDate) }}</span>
                  </td>
                  <td class="table-cell">
                    <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold">{{ getWerkLabel(doc) }}</span>
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center gap-1">
                      @if (canEdit) {
                        <button
                          type="button"
                          class="p-2 rounded-lg text-text-muted hover:text-primary flex items-center justify-center hover:bg-white/10 transition-all"
                          (click)="openEditPage(doc); $event.stopPropagation()"
                          title="Bearbeiten"
                        >
                          <span class="material-icons text-lg">edit</span>
                        </button>
                      }
                      @if (canDelete) {
                        <button
                          type="button"
                          class="p-2 rounded-lg text-text-muted hover:text-red-400 flex items-center justify-center hover:bg-red-400/10 transition-all"
                          (click)="onDelete(doc); $event.stopPropagation()"
                          title="Löschen"
                        >
                          <span class="material-icons text-lg">delete</span>
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (totalItems > 0) {
        <div class="px-6 py-4 border-t border-white/10">
          <app-pagination
            [currentPage]="currentPage"
            [totalPages]="totalPages"
            [totalItems]="totalItems"
            [pageSize]="pageSize"
            (pageChange)="onPageChange($event)"
          ></app-pagination>
        </div>
      }
    }
  </div>
</div>
